"use client";

import { Config } from "@puckeditor/core";
import { SiteHeader } from "@/components/SiteHeader";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";

// Shaaira Custom Puck Blocks
import { AnnouncementBarBlock } from "./blocks/AnnouncementBarBlock";
import { ShaairaHeroBlock } from "./blocks/ShaairaHeroBlock";
import { CategoryGridBlock } from "./blocks/CategoryGridBlock";
import { ServicesGridBlock } from "./blocks/ServicesGridBlock";
import { BatchEstimatorBlock } from "./blocks/BatchEstimatorBlock";
import { RegulatoryMatrixBlock } from "./blocks/RegulatoryMatrixBlock";
import { ProcessTimelineBlock } from "./blocks/ProcessTimelineBlock";
import { TestimonialsBlock } from "./blocks/TestimonialsBlock";
import { FAQAccordionBlock } from "./blocks/FAQAccordionBlock";
import { CTABandBlock } from "./blocks/CTABandBlock";
import { SiteFooterBlock } from "./blocks/SiteFooterBlock";
import { CategoryDetailBlock } from "./blocks/CategoryDetailBlock";
import { AboutContentBlock } from "./blocks/AboutContentBlock";
import { ContactFormBlock } from "./blocks/ContactFormBlock";
import { ProductCatalogBlock } from "./blocks/ProductCatalogBlock";
import { PageHeroBlock } from "./blocks/PageHeroBlock";
import { TextBlockConfig } from "./blocks/TextBlock";
import { InlineImageBlockConfig } from "./blocks/InlineImageBlock";
import { SpacerBlockConfig } from "./blocks/SpacerBlock";
import { SectionBlockConfig } from "./blocks/SectionBlock";
import { FeatureGridBlockConfig } from "./blocks/FeatureGridBlock";
import { IconBenefitsBlockConfig } from "./blocks/IconBenefitsBlock";
import { ComplianceBadgesConfig } from "./blocks/ComplianceBadges";
import { DownloadCenterBlockConfig } from "./blocks/DownloadCenterBlock";
import { SuccessStoryBlockConfig } from "./blocks/SuccessStoryBlock";
import { ConfirmationBlock } from "./blocks/ConfirmationBlock";
import { BlogIndexBlockConfig } from "./blocks/BlogIndexBlock";
import { withContentBlockControls } from "./withContentBlockControls";

function LiveManagedHeader() {
  const convexConfigured = Boolean(process.env.NEXT_PUBLIC_CONVEX_URL);
  const settings = useQuery(api.siteSettings.getPublic, convexConfigured ? {} : "skip");
  return <SiteHeader initialSettings={settings} showAnnouncement />;
}

const editableBlocks = {
  ShaairaHero: withContentBlockControls(ShaairaHeroBlock),
  CategoryGrid: withContentBlockControls(CategoryGridBlock, { structuredData: "item-list" }),
  ServicesGrid: withContentBlockControls(ServicesGridBlock, { structuredData: "service-list" }),
  BatchEstimator: withContentBlockControls(BatchEstimatorBlock),
  RegulatoryMatrix: withContentBlockControls(RegulatoryMatrixBlock),
  ProcessTimeline: withContentBlockControls(ProcessTimelineBlock, { structuredData: "how-to" }),
  Testimonials: withContentBlockControls(TestimonialsBlock),
  FAQAccordion: withContentBlockControls(FAQAccordionBlock, { structuredData: "faq" }),
  CTABand: withContentBlockControls(CTABandBlock),
  CategoryDetail: withContentBlockControls(CategoryDetailBlock, { structuredData: "item-list" }),
  AboutContent: withContentBlockControls(AboutContentBlock),
  ContactForm: withContentBlockControls(ContactFormBlock),
  ProductCatalog: withContentBlockControls(ProductCatalogBlock),
  PageHero: withContentBlockControls(PageHeroBlock),
  TextBlock: withContentBlockControls(TextBlockConfig),
  InlineImage: withContentBlockControls(InlineImageBlockConfig),
  Section: withContentBlockControls(SectionBlockConfig),
  Spacer: withContentBlockControls(SpacerBlockConfig),
  FeatureGrid: withContentBlockControls(FeatureGridBlockConfig),
  IconBenefits: withContentBlockControls(IconBenefitsBlockConfig),
  ComplianceBadges: withContentBlockControls(ComplianceBadgesConfig),
  DownloadCenter: withContentBlockControls(DownloadCenterBlockConfig),
  SuccessStory: withContentBlockControls(SuccessStoryBlockConfig),
  Confirmation: withContentBlockControls(ConfirmationBlock),
  BlogIndex: withContentBlockControls(BlogIndexBlockConfig),
};

export const config: Config = {
  root: {
    fields: {
      title: { type: "text", label: "Page Title (SEO)" },
      description: { type: "textarea", label: "Meta Description" },
      keywords: { type: "text", label: "Keywords (Comma separated)" },
      ogImage: { type: "text", label: "OG Image URL" },
      schemaType: {
        type: "select",
        label: "Structured Data Schema",
        options: [
          { label: "None", value: "none" },
          { label: "Organization", value: "organization" },
          { label: "Service", value: "service" },
          { label: "FAQ Page", value: "faq" },
          { label: "How-To / Process", value: "how-to" },
          { label: "Product Collection", value: "product-list" },
          { label: "Article Collection", value: "collection" },
          { label: "Contact Page", value: "contact" },
          { label: "About Page", value: "about" },
          { label: "Standard Web Page", value: "web-page" },
        ],
      },
    },
    defaultProps: {
      title: "Shaaira Biocare — Private-Label Cosmeceutical Manufacturing",
      description: "Low-MOQ private-label skin, hair, body, baby, grooming, oral, pet, hygiene, and curated kit manufacturing.",
      keywords: "private label cosmetics, cosmeceutical manufacturer, low MOQ cosmetic manufacturing",
      ogImage: "/assets/og-image.svg",
      schemaType: "none",
    },
    render: ({ children }: any) => {
      return (
        <div className="flex flex-col min-h-screen font-sans bg-transparent">
          <LiveManagedHeader />
          <main className="flex-grow">{children}</main>
        </div>
      );
    },
  },
  categories: {
    "Header & Navigation": {
      components: ["AnnouncementBar"],
    },
    "Hero Sections": {
      components: ["ShaairaHero", "PageHero"],
    },
    "Product Categories": {
      components: ["CategoryGrid", "CategoryDetail", "ProductCatalog"],
    },
    "Services & Capabilities": {
      components: ["ServicesGrid", "BatchEstimator", "RegulatoryMatrix"],
    },
    "Process & Proof": {
      components: ["ProcessTimeline", "Testimonials", "FAQAccordion"],
    },
    "Calls to Action": {
      components: ["CTABand", "ContactForm", "Confirmation"],
    },
    "Pages & Content": {
      components: ["AboutContent", "BlogIndex", "TextBlock", "InlineImage", "Section", "Spacer"],
    },
    "Evidence & Resources": {
      components: ["FeatureGrid", "IconBenefits", "ComplianceBadges", "DownloadCenter", "SuccessStory"],
    },
    Footer: {
      components: ["SiteFooterBlock"],
    },
  },
  components: {
    // Core Shaaira Biocare Blocks
    AnnouncementBar: AnnouncementBarBlock,
    ShaairaHero: editableBlocks.ShaairaHero,
    CategoryGrid: editableBlocks.CategoryGrid,
    ServicesGrid: editableBlocks.ServicesGrid,
    BatchEstimator: editableBlocks.BatchEstimator,
    RegulatoryMatrix: editableBlocks.RegulatoryMatrix,
    ProcessTimeline: editableBlocks.ProcessTimeline,
    Testimonials: editableBlocks.Testimonials,
    FAQAccordion: editableBlocks.FAQAccordion,
    CTABand: editableBlocks.CTABand,
    SiteFooterBlock: SiteFooterBlock,
    CategoryDetail: editableBlocks.CategoryDetail,
    AboutContent: editableBlocks.AboutContent,
    ContactForm: editableBlocks.ContactForm,
    ProductCatalog: editableBlocks.ProductCatalog,
    PageHero: editableBlocks.PageHero,
    TextBlock: editableBlocks.TextBlock,
    InlineImage: editableBlocks.InlineImage,
    Section: editableBlocks.Section,
    Spacer: editableBlocks.Spacer,
    FeatureGrid: editableBlocks.FeatureGrid,
    IconBenefits: editableBlocks.IconBenefits,
    ComplianceBadges: editableBlocks.ComplianceBadges,
    DownloadCenter: editableBlocks.DownloadCenter,
    SuccessStory: editableBlocks.SuccessStory,
    Confirmation: editableBlocks.Confirmation,
    BlogIndex: editableBlocks.BlogIndex,
  },
};

// Public rendering keeps compatibility with archived/imported block names, but
// these aliases stay out of Puck so editors see one intentional block per job.
export const renderConfig: Config = {
  ...config,
  components: {
    ...config.components,
    HeroCarousel: editableBlocks.ShaairaHero,
    ModernHero: editableBlocks.ShaairaHero,
    Hero: editableBlocks.ShaairaHero,
    BentoGridHero: editableBlocks.ShaairaHero,
    LayeredDepthHero: editableBlocks.ShaairaHero,
    OrbitalHero: editableBlocks.ShaairaHero,
    StackedCardHero: editableBlocks.ShaairaHero,
    SwissStyleHero: editableBlocks.ShaairaHero,
    ModernServices: editableBlocks.ServicesGrid,
    Services: editableBlocks.ServicesGrid,
    ServiceGrid: editableBlocks.ServicesGrid,
    BentoServices: editableBlocks.ServicesGrid,
    ModernStats: editableBlocks.ShaairaHero,
    Stats: editableBlocks.ShaairaHero,
    CategoryPortfolio: editableBlocks.CategoryGrid,
    CatalogSection: editableBlocks.CategoryGrid,
    HomeEssentials: editableBlocks.CategoryGrid,
    ProductCategoryCarousel: editableBlocks.CategoryGrid,
    ModernTestimonials: editableBlocks.Testimonials,
    Testimonial: editableBlocks.Testimonials,
    TestimonialsSlider: editableBlocks.Testimonials,
    Footer: SiteFooterBlock,
    SiteFooter: SiteFooterBlock,
    FAQ: editableBlocks.FAQAccordion,
    CallToAction: editableBlocks.CTABand,
    CTA: editableBlocks.CTABand,
  },
};
